import React, {
    useCallback, useMemo
} from 'react'
import { chain } from 'lodash'
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import Button from 'components/Button'
import { addModal } from 'features/modals/modalSlice'
import { colors } from 'utils'
import { findMaxOrder, NavigationItem, NavigationMenu } from '.'
import {
    eventLogoSelector
} from '../../auth/eventPreviewSlice'
import InfoPlaceholder from './InfoPlaceholder'
import NavItem from './NavItem'
import NavMenu from './NavMenu'
import NavMenuEditor from './NavMenuEditor'

const S = {
    NavBar: styled.div`
        display: flex;
        align-items: center;
        width: 100%;
        height: 60px;
        padding: 10px 20px;
        background: #FFFFFF;
        box-shadow: 0px 1px 4px rgba(0, 0, 0, 0.1);
    `,
    LogoWrapper: styled.div`
        min-width: 150px;
        max-width: 400px;
        height: 100%;
    `,
    NavLogo: styled.img`
        max-width: 400px;
        height: 100%;
    `,
    Divider: styled.div`
        width: 0;
        height: 28px;
        margin: 0 5px;
        border-left: 1px solid ${colors.iconOpacity(0.1)};
    `,
    MenuWrapper: styled.div`
        display: flex;
        padding: grid;
        overflow: visible;
    `,
    DraggableWrapper: styled.div`
        display: flex;
    `,
    CreateButton: styled(Button)`
        background: transparent;
        border-radius: 50%;
        border: 1px dashed ${colors.icon};
        padding: 0;
        width: 20px;
        height: 20px;
        display: flex;
        justify-content: center;
        align-items: center;
        margin-left: 10px;
    `,
}

type TopNavProps = {
    navigationMenus: NavigationMenu[]
    navigationItems: NavigationItem[]
    onDeletedMenuItem: () => void
    onDraggedMenuItem: (indexFrom, indexTo) => void
    onAddedMenuItem: () => void
}
const TopNav = ({
    navigationMenus,
    navigationItems,
    onDraggedMenuItem,
    onDeletedMenuItem,
    onAddedMenuItem,
}: TopNavProps) => {
    const logo = useSelector(eventLogoSelector)
    const dispatch = useDispatch()

    const navigationMenuViews = useMemo(() => chain([...navigationItems, ...navigationMenus])
        .sortBy('order')
        .map((nav, index) => {
            const navMenu = nav as unknown as NavigationMenu
            const navItem = nav as unknown as NavigationItem
            return (
                <S.DraggableWrapper key={nav.id}>
                    {index !== 0 && <S.Divider />}
                    <Draggable draggableId={`${nav.id}`} index={index}>
                        {(provided) => (
                            <div ref={provided.innerRef} {...provided.draggableProps}>
                                {navMenu.subMenus ? (
                                    <NavMenu
                                        {...provided.dragHandleProps}
                                        menu={navMenu}
                                        onDeleted={onDeletedMenuItem}
                                    />
                                ) : (
                                    <NavItem
                                        {...provided.dragHandleProps}
                                        item={navItem}
                                        onDeleted={onDeletedMenuItem}
                                    />
                                )}

                            </div>
                        )}
                    </Draggable>
                </S.DraggableWrapper>
            )
        })
        .value(), [navigationItems, navigationMenus, onDeletedMenuItem])

    const handleDragEnd = useCallback(({ destination, source }) => {
        onDraggedMenuItem(source?.index, destination?.index)
    }, [onDraggedMenuItem])

    const showCreateMenuItemModal = useCallback(() => {
        dispatch(addModal({
            Component: ({ close }: { close: () => void }) => (
                <NavMenuEditor
                    close={close}
                    newOrder={findMaxOrder([...navigationItems, ...navigationMenus]) + 1}
                    onSubmitted={onAddedMenuItem}
                />
            ),
            shouldNotWrap: true
        }))
    }, [dispatch, navigationItems, navigationMenus, onAddedMenuItem])

    return (
        <S.NavBar>
            <S.LogoWrapper>
                <S.NavLogo src={logo} />
            </S.LogoWrapper>
            <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="droppable" direction="horizontal">
                    {(provided) => (
                        <S.MenuWrapper ref={provided.innerRef} {...provided.droppableProps}>
                            {navigationMenuViews}
                            {provided.placeholder}
                        </S.MenuWrapper>
                    )}
                </Droppable>
            </DragDropContext>
            {navigationMenuViews.length > 0 && navigationMenuViews.length < 3 && <S.Divider />}
            {
                navigationMenuViews.length < 3 && (
                    <S.CreateButton
                        icon="plus"
                        onClick={showCreateMenuItemModal}
                        iconProps={{ color: colors.icon, size: 20, style: { marginBottom: 2 } }}
                    />
                )
            }
            <InfoPlaceholder />
        </S.NavBar>
    )
}

export default TopNav
